








































import { computed, defineComponent, ref } from "@vue/composition-api";
import { debounce } from "lodash-es";
import { PayeeInformation } from "@scrile/api-provider/dist/api/PayeeInformationProvider";
import { PayoutRequestSettings } from "@scrile/api-provider/dist/api/PayoutRequestsProvider";
import { PayoutSystemSettings } from "@scrile/api-provider/dist/api/PayoutSystemSettingsProvider";
import { CREDITS_CURRENCY_CODE } from "@scrile/tools/dist/lib/MoneyHelpers";
import useAuthUser from "@/hooks/useAuthUser";
import providers from "@/lib/api";
import { handleRequestError } from "@/lib/handleRequestError";
import { $t } from "@/lib/i18nHelpers";
import { formatMoneyString, getCurrencySymbol } from "@/lib/moneyHelpers";
import BaseButton from "@/components/BaseButton";
import BaseConfirm from "@/components/BaseConfirm";
import CreatePayoutRequestModal from "./CreatePayoutRequestModal";

const FIELD_AMOUNT_ERROR = "{javax.validation.constraints.Min.message}";
const NOT_ENOUGH_AMOUNT_ERROR = "{com.scrile.money.validation.CreatePayoutRequestMinimumAmountValidator.message}";
const NOT_ENOUGH_FUNDS_ERROR = "{com.scrile.money.validation.CreatePayoutRequestBalanceValidator.message}";
const METHOD_UNAVAILABLE_ERROR = "{com.scrile.payoutsystems.masspay.method-unavailable.message}";
const METHOD_NOT_SUPPORT_AMOUNT_ERROR = "{com.scrile.payoutsystems.masspay.method-not-support-amount.message}";

export default defineComponent({
  name: "AddNewRequest",
  components: { BaseButton, BaseConfirm, CreatePayoutRequestModal },
  props: {
    isEmptyRequests: { type: Boolean, default: false },
    primaryBalance: { type: Number, required: true },
  },
  setup(props, { emit, root }) {
    const { authUserStrict } = useAuthUser();
    const isShowModal = ref(false);
    const isShowMethodUnavailable = ref(false);
    const loading = ref(false);
    const inputValue = ref("");
    const externalFee = ref(0);
    const externalFeeLoading = ref(false);
    const payeeInfo = ref<PayeeInformation | null>(null);
    const payoutSystemSettings = ref<PayoutSystemSettings[]>([]);
    const currencySymbol = $t(getCurrencySymbol(CREDITS_CURRENCY_CODE));
    const requestErrorValue = ref("");
    const allowSend = computed(() => inputValue.value.length !== 0 && +inputValue.value > 0);

    const requestSettings = ref<PayoutRequestSettings | null>(null);
    const getOwnRequestSettings = async () => {
      await Promise.all([
        providers.PayoutRequestsProvider.getSettings({
          data: { userIds: [authUserStrict.value.id] },
        }).then(r => (requestSettings.value = r.find(i => i.userId === authUserStrict.value.id) ?? null)),
        providers.PayeeInformationProvider.find({ id: authUserStrict.value.id }).then(r => (payeeInfo.value = r)),
        providers.PayoutSystemSettingsProvider.findAll().then(r => (payoutSystemSettings.value = r)),
      ]);
    };
    getOwnRequestSettings();

    const payoutSystemName = computed(
      () => payoutSystemSettings.value.find(system => system.systemName === payeeInfo.value?.payoutSystem) || null
    );

    const adminCommission = computed(() => {
      const amount = Number(inputValue.value);
      const percentCommission = payoutSystemName.value?.percentCommission ?? 0;
      const flatCommission = payoutSystemName.value?.flatCommission ?? 0;
      return isNaN(amount) ? 0 : (amount / 100) * percentCommission + flatCommission;
    });

    const commission = computed(() => adminCommission.value + externalFee.value);

    const goToPayeeInfoEdit = () => root.$router.push({ name: "profilePayeeInfoEdit" });

    const handleNewRequest = async () => {
      if (payoutSystemName.value?.systemName === "masspay") {
        isShowModal.value = true;
      } else {
        await goToPayeeInfoEdit();
      }
    };

    const subText = computed(() => {
      if (requestErrorValue.value) {
        return $t(requestErrorValue.value);
      }

      return $t("Minimum withdrawal amount is {0}", [formatMoneyString(requestSettings.value?.minimumAmount ?? 0)]);
    });

    const checkError = (e: unknown) =>
      handleRequestError(e, [
        {
          comparator: e =>
            [
              FIELD_AMOUNT_ERROR,
              NOT_ENOUGH_FUNDS_ERROR,
              NOT_ENOUGH_AMOUNT_ERROR,
              METHOD_NOT_SUPPORT_AMOUNT_ERROR,
            ].includes(e.details?.messageTemplate || ""),
          onSuccessCB: e => (requestErrorValue.value = e.message ?? "Error"),
        },
        {
          comparator: e => e.details?.messageTemplate === METHOD_UNAVAILABLE_ERROR,
          onSuccessCB: () => {
            isShowModal.value = false;
            isShowMethodUnavailable.value = true;
          },
        },
      ]);

    const onCreateRequest = async () => {
      if (!allowSend.value) return;
      try {
        loading.value = true;
        providers.PayoutRequestsProvider.setReceiveErrors(false);
        const r = await providers.PayoutRequestsProvider.create({
          data: {
            amount: +inputValue.value,
            userId: authUserStrict.value.id,
          },
        });
        inputValue.value = "";
        emit("createRequest", r);
      } catch (e) {
        checkError(e);
      } finally {
        loading.value = false;
      }
    };

    const onCloseModal = () => {
      inputValue.value = "";
      isShowModal.value = false;
      requestErrorValue.value = "";
    };

    const getExternalFee = debounce(async () => {
      try {
        const amount = +inputValue.value - adminCommission.value;
        if (amount <= 0) {
          externalFee.value = 0;
          return;
        }
        if (payoutSystemName.value?.systemName === "masspay") {
          providers.PayeeInformationProvider.setReceiveErrors(false);
          externalFee.value = (
            await providers.PayeeInformationProvider.getMasspayPrePayoutInfo({ data: { amount } })
          ).fee;
        } else {
          externalFee.value = 0;
        }
      } catch (e) {
        externalFee.value = 0;
        checkError(e);
      } finally {
        externalFeeLoading.value = false;
      }
    }, 500);

    const onInput = async (v: string) => {
      const value = +v;
      requestErrorValue.value = "";
      inputValue.value = isNaN(value) || value === 0 ? "" : value.toString();

      externalFeeLoading.value = true;
      await getExternalFee();
    };

    return {
      loading,
      isShowModal,
      isShowMethodUnavailable,
      inputValue,
      currencySymbol,
      requestSettings,
      subText,
      requestErrorValue,
      commission,
      externalFeeLoading,
      onCreateRequest,
      handleNewRequest,
      onCloseModal,
      goToPayeeInfoEdit,
      onInput,
    };
  },
});
